import React, { useContext, useEffect, useMemo, useState } from 'react';

import { Box, Typography } from '@material-ui/core';
import { GridColDef } from '@mui/x-data-grid';
import { useHistory } from 'react-router-dom';

import { AppSearchInput } from '@vyce/core/src/components/inputs';
import { DeviceContext } from '@vyce/core/src/contexts';
import { buildFormUrlSegment, getPaddingForContent, getUKFormattedDate } from '@vyce/core/src/utils';
import {
  AppDataGridWithSavedPage,
  DetailsIcon,
  FormattedDescription,
  GridCellWithAvatar,
} from '@vyce/core/src/components';
import useChatRequests from '@vyce/core/src/hooks/useChatRequests';
import { ChatUser } from '@vyce/core/src/types';

import { useFormList } from './hooks/useFormList';
import { User } from '../../types';

export const FormsToAnswer: React.FC<{ user: User; companyId: string }> = ({ user, companyId }) => {
  const { isMobile } = useContext(DeviceContext);
  const padding = getPaddingForContent(isMobile);
  const [search, setSearch] = useState<string>('');
  const history = useHistory();
  const { createPrivateChat } = useChatRequests();

  const chatCreator: ChatUser = {
    uid: user.uuid,
    name: `${user.first_name} ${user.last_name}`,
    email: user.email,
    phone: user.phone,
    avatarUrl: user.photo?.url,
  };

  const goToChat = async (sendBy: ChatUser) => {
    if (!chatCreator || !companyId) {
      return;
    }
    await createPrivateChat({
      creator: chatCreator,
      chatUser: sendBy,
      companyId,
    });
  };

  const { forms, loading, getFormsToAnswer, total } = useFormList({ user, companyId });

  const columns: GridColDef[] = [
    {
      field: 'title',
      headerName: 'Form Title',
      flex: 0.2,
      minWidth: 140,
      disableColumnMenu: true,
      renderCell: params => (
        <Box display="flex" width="100%" gridGap={8} justifyContent="space-between" alignItems="center">
          <Typography
            style={{
              fontWeight: 500,
              fontSize: 13,
              display: '-webkit-box',
              WebkitLineClamp: 3,
              WebkitBoxOrient: 'vertical',
              overflow: 'hidden',
              whiteSpace: 'normal',
              wordBreak: 'break-word',
              lineHeight: 1.4,
              flexGrow: 1,
              minWidth: 0,
            }}
          >
            {params.row.title}
          </Typography>

          {params.row.description && (
            <DetailsIcon
              content={<FormattedDescription text={params.row.description} variant="caption" />}
              type="info"
              richContent
            />
          )}
        </Box>
      ),
    },
    {
      field: 'status',
      headerName: 'Status',
      flex: 0.2,
      minWidth: 140,
      disableColumnMenu: true,
      renderCell: params => (
        <Box display="flex" width="100%" justifyContent="space-between">
          <Typography style={{ fontWeight: 500, fontSize: 13 }}>To Do</Typography>
        </Box>
      ),
    },
    {
      field: 'endDate',
      headerName: 'Due date',
      flex: 0.2,
      minWidth: 140,
      disableColumnMenu: true,
      renderCell: params => (
        <Box display="flex" width="100%" justifyContent="space-between">
          <Typography style={{ fontWeight: 500, fontSize: 13 }}>
            {params.row.endDate ? getUKFormattedDate(params.row.endDate._seconds * 1000) : '-'}
          </Typography>
        </Box>
      ),
    },
    {
      field: 'sendBy',
      headerName: 'Send by',
      flex: 0.2,
      minWidth: 140,
      disableColumnMenu: true,
      renderCell: params => (
        <Box display="flex" width="100%" justifyContent="space-between">
          {params.row.sendBy ? (
            <GridCellWithAvatar
              hideAvatar
              name={params.row.sendBy.name}
              createPrivateChat={() => goToChat(params.row.sendBy)}
            />
          ) : (
            '-'
          )}
        </Box>
      ),
    },
  ];

  const searchedForms = useMemo(() => {
    return search ? forms.filter(form => form.title.toLowerCase().includes(search.toLowerCase())) : forms;
  }, [forms, search]);

  useEffect(() => {
    getFormsToAnswer();
  }, []);

  return (
    <Box padding={padding}>
      <Box display="flex" justifyContent="space-between" alignItems="center" gridGap={16} mb={2}>
        <AppSearchInput
          placeholder="Search..."
          onChange={e => setSearch(e.target.value)}
          isSmall
          smallRounding
          isBorder
          expanded={true}
        />
      </Box>

      <AppDataGridWithSavedPage
        rows={searchedForms}
        getRowId={row => row.id}
        columns={columns}
        noPaper
        rowHeight={88}
        height="calc(100vh - 240px)"
        loading={loading}
        rowCount={total}
        pageSize={100}
        rowsPerPageOptions={[100]}
        onPageChange={() => {}}
        disableSelectionOnClick
        setOffset={() => {}}
        onRowClick={params => {
          history.push(`/forms/form-to-answer/${buildFormUrlSegment(params.row.title, params.row.id)}`);
        }}
      />
    </Box>
  );
};
